import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  HStack,
  Image,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IBook } from '@partiu/api/types';
import { useEffect, useRef, useState } from 'react';
import { BiPause, BiPlay, BiSkipNext, BiSkipPrevious } from 'react-icons/bi';
import { useLocation } from 'react-router-dom';
import { toDuration } from '../../../../packages/ui/formatters';
import { MainTemplate } from '../templates/MainTemplate';
import { getBookPosition, saveBookProgress } from '../utils';

export function AudioBookPage() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [book, setBook] = useState<IBook>();
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioIndex, setAudioIndex] = useState(0);
  const { state } = useLocation();
  const drawerDisclosure = useDisclosure();

  const sliderMarkProps = {
    fontSize: '12px',
    color: '#a9a9a9',
    marginTop: '5px',
  };

  useEffect(() => {
    setAudioIndex(getBookPosition(state.book.id));
    if (state.book) setBook(state.book);
  }, [state]);

  useEffect(() => {
    const audio = audioRef.current;
    if (isPlaying) audio?.play();
    else audio?.pause();
  }, [isPlaying]);

  useEffect(() => {
    if (!audioRef.current || !book) return;

    audioRef.current.currentTime = 0;
    setProgress(0);
    setIsPlaying(false);

    saveBookProgress(
      book,
      Math.floor(((audioIndex + 1) * 100) / book.audiobooks.length).toString(),
      audioIndex.toString()
    );
  }, [audioIndex]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const interval = setInterval(() => {
      setProgress((audio.currentTime * 100) / audio.duration);
    }, 1000);

    return () => clearInterval(interval);
  }, [audioRef, audioIndex, isPlaying]);

  if (!book) return;

  return (
    <MainTemplate hideBottomMenu>
      <Drawer placement="left" {...drawerDisclosure}>
        <DrawerOverlay backdropFilter={'blur(10px)'} />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Sessões</DrawerHeader>
          <DrawerBody>
            <VStack align={'flex-start'} spacing={2}></VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <VStack paddingTop={'40px'} width="100%" backdropFilter={'blur(10px)'} justify={'flex-start'}>
        <VStack spacing={5} align="flex-start" justify={'center'} width="90%">
          <Image margin="auto" aspectRatio={1} objectFit={'contain'} height={'50vh'} src={book.cover} />
          <Heading
            paddingTop={'10px'}
            whiteSpace={'nowrap'}
            textOverflow={'ellipsis'}
            overflowX={'clip'}
            w={'100%'}
            fontSize={'16px'}
          >
            {book.title}
          </Heading>
          {book?.author && (
            <Heading marginTop={'-16px'} fontWeight={500} fontSize={'14px'}>
              {book?.author}
            </Heading>
          )}

          <Heading paddingBottom={'10px'} marginTop={'-16px'} fontWeight={500} fontSize={'14px'}>
            Capítulo {audioIndex + 1} de {book.audiobooks.length}
          </Heading>
          <audio key={audioIndex} ref={audioRef} style={{ display: 'none' }} controls>
            <source src={book.audiobooks.sort((a, b) => a.position - b.position)[audioIndex].file} />
          </audio>
          <Slider
            onChange={(value) => {
              if (!audioRef.current?.currentTime) return;
              audioRef.current.currentTime = (value / 100) * (audioRef.current?.duration || 0);
            }}
            value={progress}
          >
            <SliderMark {...sliderMarkProps} value={0}>
              {toDuration(audioRef.current?.currentTime || 0)}
            </SliderMark>
            <SliderMark {...sliderMarkProps} transform={'translateX(-100%)'} value={100}>
              {toDuration(audioRef.current?.duration || 0)}
            </SliderMark>
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <HStack paddingTop={'20px'} width="60%" margin="auto" justify={'space-between'}>
            <BiSkipPrevious
              color={audioIndex === 0 ? '#a9a9a9' : 'black'}
              cursor="pointer"
              onClick={() => setAudioIndex(Math.max(audioIndex - 1, 0))}
              fontSize={'40px'}
            />
            <Box cursor={'pointer'} onClick={() => setIsPlaying(!isPlaying)}>
              {isPlaying ? <BiPause fontSize={'50px'} /> : <BiPlay fontSize={'50px'} />}
            </Box>
            <BiSkipNext
              color={audioIndex === book.audiobooks.length - 1 ? '#a9a9a9' : 'black'}
              cursor={'pointer'}
              onClick={() => setAudioIndex(Math.min(audioIndex + 1, book.audiobooks.length - 1))}
              fontSize={'40px'}
            />
          </HStack>
        </VStack>
      </VStack>
      <VStack width={'100%'}></VStack>
    </MainTemplate>
  );
}
